import type { ElementType } from 'react';
import styled, { css, keyframes } from 'styled-components';

import {
  DangerIcon,
  InfoColorIcon,
  SuccessFilledIcon,
  WarningColorIcon,
} from '@genially/icons';

import { BasicParagraph } from '../../../../../../../foundations/typefaces/BasicParagraph';

interface BasicParagraphProps {
  $color?: string;
}

interface WrapperProps {
  $backgroundColor?: string;
}

export const IconWrapper = styled.i(({ theme: { size } }) => ({
  width: size.units(2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
}));

export const StyledFilledSuccessIcon = styled(SuccessFilledIcon)(
  ({ theme: { color } }) => ({
    fill: color.content.success.default(),
  }),
);

export const StyledErrorIcon = styled(DangerIcon)(({ theme: { color } }) => ({
  fill: color.content.error.default(),
}));

export const StyledWarningColorIcon = styled(WarningColorIcon)(
  ({ theme: { color } }) => ({
    '& path:first-child': {
      fill: color.content.warning.default(),
    },
  }),
);

export const StyledInfoColorIcon = styled(InfoColorIcon)(({ theme: { color } }) => ({
  '& circle': {
    fill: color.content.info.default(),
  },

  '& path': {
    fill: color.content.reversed.default(),
  },
}));

const animateIn = () =>
  keyframes({
    '0%': {
      transform: 'translateX(80px)',
      opacity: '0',
    },
    '100%': {
      transform: 'translateX(0)',
      opacity: '1',
    },
  });

const animateOut = () =>
  keyframes({
    '0%': {
      transform: 'translateX(0)',
      opacity: '1',
    },
    '100%': {
      transform: 'translateX(80px)',
      opacity: '0',
    },
  });

const inAnimation = '0.2s cubic-bezier(0,0,0.2,1.2) forwards';

const outAnimation = '0.3s cubic-bezier(0.6,0,0,1)';

const withAnimation = (Component: ElementType) =>
  styled(Component)<{ $dead: boolean }>(
    ({ $dead }) => css`
      animation: ${$dead ? animateOut : animateIn} ${$dead ? outAnimation : inAnimation};
    `,
  );

export const Wrapper = withAnimation(
  styled.div<WrapperProps>(({ theme: { color, size }, $backgroundColor }) => ({
    width: size.units(40),
    display: 'flex',
    alignItems: 'flex-start',
    backgroundColor: $backgroundColor || color.background.reversed.default(),
    padding: size.units(2),
    gap: size.units(1),
    borderRadius: size.units(1),
    transform: 'translateX(80px)',
    opacity: '0',

    [IconWrapper]: {
      marginTop: size.units(0.25),
    },
  })),
);

export const StyledBasicParagraph = styled(BasicParagraph)<BasicParagraphProps>(
  ({ theme: { color, transition }, $color }) => ({
    color: $color || color.content.reversed.default(),

    a: {
      textDecoration: 'underline',
      ':hover': {
        color: color.content.reversed.hover(),
        transition: transition.standard(),
      },
    },
  }),
);
